import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { deliveryAPI, jobsAPI } from '@/services/api';
import { uploadFileWithTus, abortTusUpload } from '@/services/tusUpload';
import UploadQueue from '@/components/user/UploadQueue';
import DeliveryFileCard from '@/components/user/DeliveryFileCard';
import JobStatusBadge from '@/components/user/JobStatusBadge';
import EmptyState from '@/components/user/EmptyState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { dedupeDeliveryFiles, needsDeliveryPolling, resolveMime } from '@/utils/deliveryHelpers';
import { toast } from 'sonner';
import { ArrowLeft, Upload } from 'lucide-react';

const makeQueueId = () => `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const VideomakerDeliveryUploadPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [processing, setProcessing] = useState(false);
  const uploadRefs = useRef({});

  const isRevisionMode = job?.status === 'revision_requested';

  const revisionFiles = useMemo(() => {
    const files = dedupeDeliveryFiles(delivery?.files || []);
    return files.filter((f) => (f.client_status || '') === 'revision_requested');
  }, [delivery]);

  const load = useCallback(async () => {
    try {
      const jobRes = await jobsAPI.getById(jobId);
      setJob(jobRes.data);
      try {
        const deliveryRes = await deliveryAPI.getByJob(jobId);
        setDelivery(deliveryRes.data);
      } catch {
        setDelivery(null);
      }
    } catch {
      toast.error('Job não encontrado.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!needsDeliveryPolling(delivery, job)) return undefined;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [delivery, job, load]);

  const ensureDelivery = async () => {
    if (delivery?.id) return delivery;
    const { data } = await deliveryAPI.create({ job_id: jobId });
    setDelivery(data);
    return data;
  };

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const maxBytes = 10 * 1024 * 1024 * 1024;
    const items = selected
      .filter((f) => f.size <= maxBytes)
      .map((f) => ({
        id: makeQueueId(),
        file: f,
        name: f.name,
        size: f.size,
        status: 'queued',
        progress: 0,
        uploadUrl: null,
        replacesFileId: null,
      }));
    if (items.length < selected.length) {
      toast.error('Alguns arquivos excedem o limite de 10 GB.');
    }
    setQueue((q) => [...q, ...items]);
    e.target.value = '';
  };

  const processQueue = async () => {
    if (!queue.some((i) => i.status === 'queued' || i.status === 'paused')) {
      toast.error('Adicione arquivos à fila primeiro.');
      return;
    }
    setProcessing(true);
    try {
      const del = await ensureDelivery();
      let revisionIndex = 0;
      const pending = queue.filter((i) => i.status === 'queued' || i.status === 'paused');
      const total = pending.length;
      let done = 0;

      for (const item of pending) {
        const current = queue.find((q) => q.id === item.id) || item;
        if (current.status === 'completed') continue;

        setQueue((q) =>
          q.map((x) => (x.id === item.id ? { ...x, status: 'uploading', error: null } : x))
        );

        try {
          const result = await uploadFileWithTus(current.file, {
            jobId,
            previousUploadUrl: current.uploadUrl || undefined,
            onProgress: (uploaded, totalBytes) => {
              const pct = totalBytes ? Math.round((uploaded / totalBytes) * 100) : 0;
              setQueue((q) =>
                q.map((x) => (x.id === item.id ? { ...x, progress: pct } : x))
              );
            },
            onUploadCreated: (url, uploadInstance) => {
              uploadRefs.current[item.id] = uploadInstance;
              setQueue((q) =>
                q.map((x) => (x.id === item.id ? { ...x, uploadUrl: url } : x))
              );
            },
          });

          const isLastFile = done === total - 1;
          const payload = {
            s3_url: result.uploadUrl,
            s3_key: result.s3Key,
            filename: current.file.name,
            file_size: current.file.size,
            content_type: resolveMime(current.file.name, current.file.type),
            is_last_file: isLastFile,
          };
          if (isRevisionMode && revisionFiles[revisionIndex]) {
            payload.replaces_file_id = revisionFiles[revisionIndex].id;
            revisionIndex += 1;
          }

          await deliveryAPI.completeUpload(del.id, payload);
          done += 1;
          setQueue((q) =>
            q.map((x) =>
              x.id === item.id ? { ...x, status: 'completed', progress: 100 } : x
            )
          );
        } catch (err) {
          setQueue((q) =>
            q.map((x) =>
              x.id === item.id
                ? { ...x, status: 'error', error: err.message || 'Falha no upload' }
                : x
            )
          );
          toast.error(err.message || 'Erro no upload.');
        }
      }

      toast.success('Processamento da fila concluído.');
      await load();
    } finally {
      setProcessing(false);
    }
  };

  const handlePause = (id) => {
    abortTusUpload(uploadRefs.current[id]);
    setQueue((q) => q.map((x) => (x.id === id ? { ...x, status: 'paused' } : x)));
  };

  const handleResume = (id) => {
    setQueue((q) => q.map((x) => (x.id === id ? { ...x, status: 'queued' } : x)));
    toast.message('Arquivo na fila. Clique em Iniciar envio para retomar.');
  };

  const handleRemove = (id) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const handleCancel = (id) => {
    abortTusUpload(uploadRefs.current[id]);
    delete uploadRefs.current[id];
    setQueue((q) => q.filter((x) => x.id !== id));
  };

  const existingFiles = dedupeDeliveryFiles(delivery?.files || []);

  if (loading) {
    return <Skeleton className="h-64 w-full bg-white/10" />;
  }

  if (!job) {
    return (
      <EmptyState title="Job não encontrado" onAction={() => navigate('/videomaker/jobs')} actionLabel="Voltar" />
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/videomaker/jobs')} className="text-white/70">
        <ArrowLeft className="h-4 w-4 mr-1" />
        Voltar
      </Button>

      <div>
        <h1 className="text-2xl font-bold">{job.titulo || 'Upload'}</h1>
        <JobStatusBadge status={job.status} />
      </div>

      {isRevisionMode && revisionFiles.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-sm text-amber-100 mb-3">Mídias com revisão solicitada:</p>
          <div className="grid gap-3 md:grid-cols-2">
            {revisionFiles.map((f) => (
              <DeliveryFileCard key={f.id} file={f} mode="view" />
            ))}
          </div>
        </div>
      )}

      {existingFiles.length > 0 && (
        <section>
          <h2 className="text-sm text-white/60 mb-3">Já enviados</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {existingFiles.map((f) => (
              <DeliveryFileCard key={f.id || f.filename} file={f} mode="view" />
            ))}
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4">
        <h2 className="font-medium flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Enviar mídias
        </h2>
        <p className="text-xs text-white/50">Não feche esta aba durante o envio. Uploads grandes podem ser retomados.</p>

        <input
          type="file"
          multiple
          accept="video/*,image/*"
          onChange={handleFilesSelected}
          className="block w-full text-sm text-white/70 file:mr-4 file:rounded-lg file:border-0 file:bg-[#0E76FF] file:px-4 file:py-2 file:text-white"
        />

        <UploadQueue
          items={queue}
          onPause={handlePause}
          onResume={handleResume}
          onRemove={handleRemove}
          onCancel={handleCancel}
        />

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={processQueue}
            disabled={processing || !queue.length}
            className="bg-[#0E76FF] hover:bg-[#2563EB]"
          >
            {processing ? 'Enviando…' : 'Iniciar envio'}
          </Button>
          <Button
            variant="outline"
            onClick={() => setQueue([])}
            disabled={processing}
            className="border-white/20"
          >
            Limpar fila
          </Button>
        </div>
      </section>
    </div>
  );
};

export default VideomakerDeliveryUploadPage;
